import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
import { useMemo, useState } from "react";
import { Box, SectionList } from "native-base";
import { firstToUpper, formatMonth, formatVerboseMonthDay, formatWeekOfYear, } from "@madmedical/utils";
import { useWindowDimensions } from "react-native";
import { useTranslation } from "@madmedical/i18n";
import DatePeriodStepper from "../molecules/DatePeriodStepper";
import { usePatientMeasurements } from "../providers/patientMeasurements";
import Headline from "../molecules/Headline";
import Paper from "../atoms/Paper";
import PatientMeasurementsTable from "../organisms/PatientMeasurementsTable";
import PatientMeasurementListItem from "../molecules/ListItem/PatientMeasurementListItem";
import PatientMeasurementsMobileFilter from "../organisms/filters/mobile/PatientMeasurementsMobileFilter";
import PatientMeasurementsDesktopFilter from "../organisms/filters/desktop/PatientMeasurementsDesktopFilter";
import SearchInput from "../molecules/Form/SearchInput";
import useResponsive from "../util/useResponsive";
import ListHeader from "../molecules/ListHeader";
const PatientMeasurements = ({ period, onPeriodChange, onEndReached, }) => {
    const { isSmallScreen } = useResponsive();
    const [flatListPosition, setFlatListPosition] = useState(0);
    const screenHeight = useWindowDimensions().height;
    const [search, setSearch] = useState("");
    const { patientMeasurements, onSearchChange } = usePatientMeasurements();
    const { t } = useTranslation();
    const handleSearchChange = (search) => {
        setSearch(search);
        onSearchChange === null || onSearchChange === void 0 ? void 0 : onSearchChange(search);
    };
    const sections = useMemo(() => {
        const groups = patientMeasurements.reduce((prev, measurement) => {
            var _a;
            const { dateTrunc } = measurement;
            const date = period === "day"
                ? firstToUpper(formatVerboseMonthDay(dateTrunc))
                : period === "week"
                    ? formatWeekOfYear(dateTrunc)
                    : firstToUpper(formatMonth(dateTrunc, "long"));
            (_a = prev[date]) !== null && _a !== void 0 ? _a : (prev[date] = []);
            prev[date].push(measurement);
            return prev;
        }, {});
        return Object.keys(groups).map((date) => ({
            header: {
                title: date,
                badge: groups[date].length,
            },
            data: groups[date],
        }));
    }, [patientMeasurements, period]);
    return (_jsxs(Paper, { children: [_jsx(Headline, { minHeight: 12, borderBottomWidth: 1, borderBottomColor: "gray.100", borderBottomStyle: "solid", title: t("measures:title"), rightContent: !isSmallScreen && (_jsx(DatePeriodStepper, { period: period, size: "xs", onPeriodChange: onPeriodChange })) }), isSmallScreen && (_jsx(Headline, { leftContent: _jsx(DatePeriodStepper, { period: period, size: "xs", onPeriodChange: onPeriodChange }) })), _jsx(Headline, { minHeight: 12, borderBottomWidth: 1, borderBottomColor: "gray.100", borderBottomStyle: "solid", leftContent: isSmallScreen ? (_jsx(SearchInput, { ml: "auto", size: "sm", value: search, onChangeText: handleSearchChange })) : (_jsx(PatientMeasurementsDesktopFilter, {})), rightContent: isSmallScreen ? (_jsx(PatientMeasurementsMobileFilter, {})) : (_jsx(SearchInput, { ml: "auto", size: "sm", value: search, onChangeText: handleSearchChange })) }), isSmallScreen ? (_jsx(_Fragment, { children: _jsx(SectionList, { onLayout: (event) => {
                        setFlatListPosition(event.nativeEvent.layout.y);
                    }, maxHeight: screenHeight - flatListPosition - 4, minWidth: "full", showsVerticalScrollIndicator: false, sections: sections, renderSectionHeader: ({ section: { header } }) => {
                        const { title, badge } = header;
                        return _jsx(ListHeader, { title: title, badge: badge });
                    }, renderItem: ({ item }) => (_jsx(PatientMeasurementListItem, { data: item, period: period })), keyExtractor: (item, index) => index.toString(), overflow: "hidden", onEndReached: onEndReached }) })) : (_jsx(Box, Object.assign({ px: 6, py: 4, width: "full" }, { children: sections.map((section, index) => (_jsxs(Box, Object.assign({ mb: 3, px: "1" }, { children: [_jsx(ListHeader, { size: "sm", title: section.header.title, badge: section.header.badge, bgColor: "white", borderBottomWidth: 0 }), _jsx(PatientMeasurementsTable, { period: period, data: section.data, onEndReached: index === sections.length - 1
                                ? onEndReached
                                : undefined })] }), `patient-measurement-section-${index}`))) })))] }));
};
export default PatientMeasurements;
